import { translate } from 'src/plugins/i18n'
import type {
  FieldInterface,
  RelationInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import { FEATURE_IDS } from 'src/tpbp-core/feature-flag/featureFlags.const'
import { hasFeature } from 'src/tpbp-core/feature-flag/featureFlags.utils'
import { FIELD_TYPES } from 'src/tpbp-core/models/fields/field-set.const'
import { FIELD_RULES } from 'src/tpbp-core/models/fields/fieldRules.const'
import { TEXT_INPUT_FORMATS } from 'src/tpbp-core/models/fields/text-input.const'
import { roundCent } from 'src/tpbp-core/utils/common.utils'
import type { ContractModel } from 'src/tpbp-json-api-handler/models/models'
import { checkChangeBeforeValidate } from '../mapping.utils'

export function getMinError({
  monthlyPaymentMin,
  monthlyPayment,
  isMonthlyPaymentMandatory,
  monthlyPaymentInitial,
  pledgeAccount,
  $hasChanged,
}: ContractModel) {
  return checkChangeBeforeValidate($hasChanged, () => {
    const monthlyMandatoryError =
      isMonthlyPaymentMandatory && monthlyPayment === 0
        ? monthlyPaymentMin || 0
        : undefined
    const havePledgeAccountError =
      pledgeAccount &&
      monthlyPaymentInitial > 0 &&
      monthlyPayment < monthlyPaymentInitial
    const pledgeAccountError = havePledgeAccountError
      ? monthlyPaymentInitial
      : monthlyMandatoryError
    const haveMinError =
      monthlyPayment > 0 && monthlyPayment < monthlyPaymentMin
    return haveMinError ? monthlyPaymentMin : pledgeAccountError
  })
}

export function getMaxDeposit({
  $isNew,
  cumulatedPaymentAmount,
  monthlyPayment,
  amountInitial,
  paymentCeiling,
  isNewContract,
  initPaymentMax,
  exceptionalPaymentMax,
  amount,
  deposit,
  $hasChanged,
}: ContractModel) {
  return checkChangeBeforeValidate($hasChanged, () => {
    const isContractNew = $isNew || isNewContract
    if (isContractNew && initPaymentMax !== null) return initPaymentMax

    const realCumulatedAmount =
      (cumulatedPaymentAmount ?? amountInitial) + monthlyPayment
    const maxAmount = realCumulatedAmount + amount - amountInitial
    const hasNewAllocControl = hasFeature(FEATURE_IDS.allocNewControl)
    if (hasNewAllocControl) {
      const maxDepositAmount =
        typeof paymentCeiling === 'number' && maxAmount > paymentCeiling
          ? paymentCeiling - realCumulatedAmount
          : undefined
      if (typeof maxDepositAmount === 'number')
        return maxDepositAmount > 0 ? maxDepositAmount : 0
    }

    return deposit > 0 ? exceptionalPaymentMax ?? undefined : undefined
  })
}

export const contractFields: FieldInterface[] = [
  {
    key: 'amount',
    originKey: 'amount',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
    format: TEXT_INPUT_FORMATS.currency2,
    expression: ({ amountInitial, deposit, withdrawal }) =>
      roundCent((amountInitial || 0) + (deposit || 0) - (withdrawal || 0)),
    controls: {
      min: ({
        amount,
        balanceMin,
        $hasChanged,
        amountInitial,
        operations,
      }: ContractModel) =>
        checkChangeBeforeValidate($hasChanged, () => {
          const haveLoiPactePayment =
            operations?.target_loi_pacte_payment?.portfolio_id !== undefined
          const haveLoiPacteAdhesion =
            operations?.target_loi_pacte_adhesion?.portfolio_id !== undefined
          const amountToAdd =
            operations?.target_loi_pacte_payment?.amount ??
            operations?.target_loi_pacte_adhesion?.amount ??
            0
          const realAmount = roundCent(amountInitial - amount)
          if (
            realAmount < amountToAdd &&
            (haveLoiPacteAdhesion || haveLoiPactePayment)
          )
            return roundCent(amountInitial + amountToAdd)
          return balanceMin && amount !== 0 && amount < balanceMin
            ? balanceMin
            : undefined
        }),
      max: ({
        $isNew,
        isNewContract,
        amount,
        maxDeposit,
        withdrawal,
        deposit,
        $hasChanged,
      }: ContractModel) =>
        checkChangeBeforeValidate($hasChanged, () => {
          const isContractNew = $isNew || isNewContract
          if (isContractNew) return

          return maxDeposit && deposit > withdrawal && amount > maxDeposit
            ? maxDeposit
            : undefined
        }),
    },
    messages: {
      min: (min: number) =>
        translate('models.contracts.amount.messages.min', { min }),
      max: (max: number) =>
        translate('models.contracts.amount.messages.max', { max }),
    },
  },
  {
    key: 'amountInitial',
    originKey: 'amount_initial',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
    format: TEXT_INPUT_FORMATS.currency2,
  },
  {
    key: 'canAddLine',
    originKey: 'product_kind_with_lines',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'canTransferLoiPacte',
    originKey: 'canTransferLoiPacte',
    type: FIELD_TYPES.boolean,
    isPseudoAttribute: true,
    expression: (record: ContractModel) => {
      return (
        (record.provider === 'internal' && record.isPacteTransfer) ||
        (record.provider === 'external' &&
          (record.possibleOperations.loi_pacte_adhesion?.available ||
            record.possibleOperations.loi_pacte_payment?.available))
      )
    },
  },
  {
    key: 'cashAccountInfos',
    originKey: 'cash_account_infos',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'contract',
    originKey: 'contract',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'contractId',
    originKey: 'contract_id',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
    dependentFieldKeys: ['canAddLine'],
  },
  {
    key: 'deposit',
    originKey: 'deposit',
    type: FIELD_TYPES.number,
    dynamicRules: [
      {
        expression: ({
          isReadonly,
          isForDeposit,
          amountInitial,
        }: ContractModel) => isReadonly || (amountInitial > 0 && !isForDeposit),
        type: FIELD_RULES.isReadOnly,
      },
    ],
    format: TEXT_INPUT_FORMATS.currency2,
    dependentFieldKeys: ['amount'],
    controls: {
      min: ({
        $isNew,
        isNewContract,
        initPaymentMin,
        exceptionalPaymentMin,
        deposit,
        $hasChanged,
      }: ContractModel) =>
        checkChangeBeforeValidate($hasChanged, () => {
          const isContractNew = $isNew || isNewContract

          if (isContractNew) return initPaymentMin
          if (deposit > 0) return exceptionalPaymentMin
          return
        }),
      max: (record: ContractModel) => getMaxDeposit(record),
    },
  },
  {
    key: 'initPaymentMin',
    originKey: 'init_payment_min',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'initPaymentMax',
    originKey: 'init_payment_max',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'eligibleUniverse',
    originKey: 'eligible_universe',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
    dependentFieldKeys: ['canAddLine'],
  },
  {
    key: 'fromTransfer',
    originKey: 'from_transfer',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'hasDicDici',
    originKey: 'has_dic_dici',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'awaitingValuation',
    originKey: 'awaiting_valuation',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'iban',
    originKey: 'iban',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isCompartmentalized',
    originKey: 'is_compartmentalized',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isDeletedContract',
    originKey: 'is_deleted_contract',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isNewContract',
    originKey: 'is_new_contract',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'haveArbitration',
    originKey: 'is_updated_contract',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'label',
    originKey: 'label',
    type: FIELD_TYPES.string,
    dependentFieldKeys: ['name'],
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'detentionMode',
    originKey: 'detention_mode',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'lclId',
    originKey: 'lcl_id',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'loiPacte',
    originKey: 'loi_pacte',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'managementType',
    originKey: 'management_type',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
    enumType: 'ApiPortfolioManagementModes',
    enumTypePath: 'src/tpbp-json-api-handler/models/contract/contract.types.ts',
  },
  {
    key: 'managementTypeInitial',
    originKey: 'management_type_initial',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
    enumType: 'ApiPortfolioManagementModes',
    enumTypePath: 'src/tpbp-json-api-handler/models/contract/contract.types.ts',
  },
  {
    key: 'mandate',
    originKey: 'mandate',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'monthlyPayment',
    originKey: 'monthly_payment',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    dynamicLabel: () =>
      translate('models.contracts.fields.monthlyPaymentAfter'),
    controls: {
      min: getMinError,
      max: ({
        monthlyPaymentMax,
        monthlyPayment,
        $hasChanged,
      }: ContractModel) =>
        checkChangeBeforeValidate($hasChanged, () =>
          monthlyPaymentMax > 0 && monthlyPayment > monthlyPaymentMax
            ? monthlyPaymentMax
            : undefined,
        ),
    },
    dynamicRules: [
      {
        expression: ({ isReadOnly, isMonthlyPaymentPossible }) =>
          !!isReadOnly || !isMonthlyPaymentPossible,
        type: FIELD_RULES.isReadOnly,
      },
    ],
  },
  {
    key: 'monthlyPaymentMin',
    originKey: 'monthly_payment_min',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
    format: TEXT_INPUT_FORMATS.currency2,
  },
  {
    key: 'monthlyPaymentMax',
    originKey: 'monthly_payment_max',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
    format: TEXT_INPUT_FORMATS.currency2,
  },
  {
    key: 'monthlyPaymentInitial',
    originKey: 'monthly_payment_initial',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
    format: TEXT_INPUT_FORMATS.currency2,
  },
  {
    key: 'monthlyBuyoutInitial',
    originKey: 'monthly_buyout_initial',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
    format: TEXT_INPUT_FORMATS.currency2,
  },
  {
    key: 'isMonthlyPaymentMandatory',
    originKey: 'is_monthly_payment_mandatory',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isMonthlyPaymentPossible',
    originKey: 'is_monthly_payment_possible',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'name',
    originKey: 'name',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isReadOnly,
    expression: ({ contract, label }: ContractModel) =>
      (contract || label) ?? '',
  },
  {
    key: 'optionId',
    originKey: 'option_id',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'options',
    originKey: 'options',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'pdfUrl',
    originKey: 'pdf_url',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'pledgeAccount',
    originKey: 'pledge_account',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isDismembered',
    originKey: 'is_dismembered',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isReadonly',
    originKey: 'is_read_only',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'producerContractId',
    originKey: 'producer_contract_id',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'productKind',
    originKey: 'product_kind',
    type: FIELD_TYPES.string,
    enumType: 'ProductKind',
    enumTypePath: 'src/services/mappings/portfolio/portfolio.const',
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'productKindWithLines',
    originKey: 'product_kind_with_lines',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'provider',
    originKey: 'provider',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'srri',
    originKey: 'srri',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'srriInitial',
    originKey: 'srri_initial',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'startDate',
    originKey: 'start_date',
    type: FIELD_TYPES.date,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'type',
    originKey: 'type',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'withdrawal',
    originKey: 'withdrawal',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency2,
    controls: {
      max: ({ amountInitial, withdrawal, $hasChanged }: ContractModel) =>
        checkChangeBeforeValidate($hasChanged, () =>
          withdrawal > 0 && withdrawal > amountInitial
            ? amountInitial
            : undefined,
        ),
    },
    dynamicRules: [
      {
        type: FIELD_RULES.isReadOnly,
        expression: ({
          isArbitrable,
          isReadonly,
          amountInitial,
        }: ContractModel) =>
          isReadonly || !isArbitrable || (amountInitial ?? 0) === 0,
      },
    ],
    dependentFieldKeys: ['amount'],
  },
  {
    key: 'lines',
    originKey: 'lines',
    type: FIELD_TYPES.object,
    objectType: 'ContractLine',
    objectTypePath:
      'src/tpbp-json-api-handler/models/contract/contract-line.types.ts',
    isMultiple: true,
  },
  {
    key: 'possibleOperations',
    originKey: 'possible_operations',
    type: FIELD_TYPES.object,
    objectType: 'ContractLinePossibleOperations',
    objectTypePath: 'src/services/mappings/contract/contract.types',
  },
  {
    key: 'isUpdatedContract',
    originKey: 'is_updated_contract',
    type: FIELD_TYPES.boolean,
  },
  {
    key: 'internal',
    originKey: 'internal',
    type: FIELD_TYPES.boolean,
  },
  {
    key: 'eligibleManagementTypes',
    originKey: 'eligibile_management_types',
    type: FIELD_TYPES.object,
    enumType: 'EligibleManagementTypes',
    enumTypePath: 'src/tpbp-json-api-handler/models/contract/contract.types.ts',
  },
  {
    key: 'isForDeposit',
    originKey: 'is_for_deposit',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'exceptionalPaymentMin',
    originKey: 'exceptional_payment_min',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'maxDeposit',
    originKey: 'max_deposit',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'exceptionalPaymentMax',
    originKey: 'exceptional_payment_max',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'balanceMin',
    originKey: 'balance_min',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'isArbitrable',
    originKey: 'is_arbitrable',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isReadOnly,
  },
  {
    key: 'resetToFreeManagement',
    originKey: 'resetToFreeManagement',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isHidden,
    isPseudoAttribute: true,
  },
  {
    key: 'maxProductCountPerHolder',
    originKey: 'max_product_count_per_holder',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'laeFormulasAccess',
    originKey: 'LAE_formulas_access',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'ageMaxComplement',
    originKey: 'age_max_complement',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'ageMaxSouscription',
    originKey: 'age_max_souscription',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'ageMinComplement',
    originKey: 'age_min_complement',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'ageMinSouscription',
    originKey: 'age_min_souscription',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'conseil',
    originKey: 'conseil',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'currency',
    originKey: 'currency',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'dateDebutCommercialisation',
    originKey: 'date_debut_commercialisation',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'endMarketDate',
    originKey: 'end_market_date',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'endStructuredDate',
    originKey: 'end_structured_date',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'excludeClientType',
    originKey: 'exclude_client_type',
    type: FIELD_TYPES.object,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'financialAssetsMin',
    originKey: 'financial_assets_min',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'guaranteeLimitAge',
    originKey: 'guarantee_limit_age',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'guaranteeLimitAmount',
    originKey: 'guarantee_limit_amount',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'hasPortfolioManagement',
    originKey: 'has_portfolio_management',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'horizon',
    originKey: 'horizon',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'horsConseil',
    originKey: 'hors_conseil',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isAdhesionByTransferPossible',
    originKey: 'is_adhesion_by_transfer_possible',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isHidden',
    originKey: 'is_hidden',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isComplex',
    originKey: 'is_complex',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isForFrenchResidents',
    originKey: 'is_for_french_residents',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isForNonProClient',
    originKey: 'is_for_non_pro_client',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isForSubscription',
    originKey: 'is_for_subscription',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isPacteTransfer',
    originKey: 'is_pacte_transfer',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'isPaymentByTransferPossible',
    originKey: 'is_payment_by_transfer_possible',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'journey',
    originKey: 'journey',
    type: FIELD_TYPES.object,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'marches',
    originKey: 'marches',
    type: FIELD_TYPES.object,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'marketDate',
    originKey: 'market_date',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'maxAge',
    originKey: 'max_age',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'maxHolders',
    originKey: 'max_holders',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'maXPctFcprAndScCapiFromInitPayment',
    originKey: 'max_pct_fcpr_and_sc_capi_from_init_payment',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'maxPctFcprFromInitPayment',
    originKey: 'max_pct_fcpr_from_init_payment',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'maxPerHolder',
    originKey: 'max_per_holder',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'minAge',
    originKey: 'min_age',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'minMonthlyPayment',
    originKey: 'min_monthly_payment',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'minPerHolder',
    originKey: 'min_per_holder',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'minUcBalance',
    originKey: 'min_uc_balance',
    type: FIELD_TYPES.object,
    rule: FIELD_RULES.isOptional,
    isMultiple: true,
  },
  {
    key: 'minUcPayment',
    originKey: 'min_uc_payment',
    type: FIELD_TYPES.object,
    rule: FIELD_RULES.isOptional,
    isMultiple: true,
  },
  {
    key: 'offerCode',
    originKey: 'offer_code',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'partMaxScpi',
    originKey: 'part_max_scpi',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'partnerCode',
    originKey: 'partner_code',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'paymentCeiling',
    originKey: 'payment_ceiling',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'qce',
    originKey: 'qce',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'cumulatedPaymentAmount',
    originKey: 'cumulated_payment_amount',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'reseauBanqueParticuliers',
    originKey: 'reseau_banque_particuliers',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'reseauBanquePrivee',
    originKey: 'reseau_banque_privee',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'reseauGestionFortune',
    originKey: 'reseau_gestion_fortune',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'reserveClientNonPro',
    originKey: 'reserve_client_non_pro',
    type: FIELD_TYPES.boolean,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'productType',
    originKey: 'product_type',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'productCategory',
    originKey: 'product_category',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'mandateManagement',
    originKey: 'mandate_management',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'clientId',
    originKey: 'client_id',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'feesRate',
    originKey: 'fees_rate',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isOptional,
  },

  {
    key: 'warningMsg',
    originKey: 'warning_msg',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
    isMultiple: true,
  },
  {
    key: 'id',
    originKey: 'id',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'operations',
    originKey: 'operations',
    type: FIELD_TYPES.object,
    objectType: 'OperationsOrUndefined',
    objectTypePath: 'src/services/mappings/contract/contract.types',
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'endDate',
    originKey: 'end_date',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'endDateInitial',
    originKey: 'end_date_initial',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'productGroupFromProductKind',
    originKey: 'product_group',
    type: FIELD_TYPES.string,
    isPseudoAttribute: true,
    expression: (record: ContractModel, { getPortfolioProductGroup }) => {
      return getPortfolioProductGroup(record.productKind) ?? ''
    },
  },
]

export const contractRelations: RelationInterface[] = []
